import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Paper, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { map } from 'lodash';
import TopBar from '../../../components/TopBar';
import CustomTextInput from '../../../components/CustomTextInput';
import ProductPricesTable from './ProductPricesTable';
import FormSelectField from '../../../components/form/FormSelectField';

const UNIT_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const TYPE_OPTIONS = [
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'postpaid', label: 'Postpaid' },
];

function buildProductSchema(readOnly) {
  return yup.object({
    name: yup.string().required('Name is required'),
    description: yup.string().nullable(),
    is_active: yup.boolean(),
    billing_interval_unit: yup.string().oneOf(['day', 'week', 'month', 'year']).required('Unit is required'),
    type: yup.string().oneOf(['prepaid', 'postpaid']).required('Type is required'),
    billing_interval_value: yup
      .number()
      .typeError('Must be a number')
      .positive('Must be positive')
      .integer('Must be a whole number')
      .required('Billing frequency is required'),
    prices: readOnly ? yup.array() : yup.array().min(1, 'At least one price is required'),
  });
}

export function normalizeProductPricesForApi(prices) {
  if (!Array.isArray(prices)) return [];
  return prices.map((row) => {
    const { id, ...rest } = row;
    let unit_amount = rest.unit_amount;
    if (unit_amount === '' || unit_amount === undefined || unit_amount === null) {
      unit_amount = undefined;
    } else {
      const n = Number(unit_amount);
      unit_amount = Number.isFinite(n) ? Math.round(n) : undefined;
    }
    let limit = rest.limit;
    if (rest.pricing_model === 'metered') {
      limit = 0;
    } else if (limit === '' || limit === undefined || limit === null) {
      limit = 0;
    } else {
      const n = Number(limit);
      limit = Number.isFinite(n) ? Math.round(n) : 0;
    }

    return {
      ...rest,
      unit_amount,
      limit,
    };
  });
}

export default function ProductForm({
  onSubmit = () => {},
  onCancel,
  initialData = {},
  apiErrors = {},
  readOnly = false,
  isLoading = false,
  isEdit = false,
}) {
  const schema = useMemo(() => buildProductSchema(readOnly), [readOnly]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      stripe_product_id: initialData.stripe_product_id || '',
      type: initialData.type || 'prepaid',
      billing_interval_unit: initialData.billing_interval_unit || 'day',
      billing_interval_value: initialData.billing_interval_value || 1,
      name: initialData.name || '',
      description: initialData.description || '',
      is_active: Boolean(initialData.is_active),
      prices: Array.isArray(initialData.prices) ? initialData.prices : [],
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const productType = watch('type');
  const previousProductTypeRef = useRef(productType);

  useEffect(() => {
    map(apiErrors, (value, key) => setError(key, { message: value, type: 'string' }));
  }, [apiErrors, setError]);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    if (previousProductTypeRef.current === productType) {
      return;
    }

    previousProductTypeRef.current = productType;
    setValue('prices', [], { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  }, [productType, readOnly, setValue]);

  const handleFormSubmit = (data) => {
    const prices = normalizeProductPricesForApi(data.prices);
    const payload = {
      ...data,
      prices,
    };
    onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Paper elevation={0} sx={{ p: '1rem' }}>
        {!readOnly && (
          <Box sx={{ pt: 2, pb: 3 }}>
            <TopBar title="Product Form" identifier={initialData.stripe_product_id}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={onCancel} disabled={isLoading} size="small">
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit(handleFormSubmit)} disabled={isLoading} size="small">
                  Save
                </Button>
              </Stack>
            </TopBar>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid size={6}>
            <CustomTextInput name="name" label="Name" control={control} errors={errors} readOnly={readOnly} />
          </Grid>
          <Grid size={6}>
            <CustomTextInput
              name="description"
              label="Description"
              control={control}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
          <Grid size={4}>
            <FormSelectField
              name="type"
              control={control}
              label="Type"
              readOnly={readOnly}
              options={TYPE_OPTIONS}
              errors={errors}
            />
          </Grid>
          <Grid size={4}>
            <CustomTextInput
              name="billing_interval_value"
              label="Billing Frequency"
              type="number"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isEdit}
              adornment="×"
            />
          </Grid>
          <Grid size={4}>
            <FormSelectField
              name="billing_interval_unit"
              control={control}
              label="Billing Unit"
              readOnly={readOnly}
              disabled={isEdit}
              options={UNIT_OPTIONS}
              errors={errors}
            />
          </Grid>
          <Grid size={12}>
            <ProductPricesTable readOnly={readOnly} isEdit={isEdit} />
            {errors.prices?.message && (
              <Box sx={{ color: 'error.main', typography: 'caption', mt: 0.5 }}>{errors.prices.message}</Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </FormProvider>
  );
}
